/**
 * NFT Bundles API Client
 *
 * Handles API calls for NFT bundles - selling multiple NFTs together for a single price.
 */

import { apiClient, ApiResponse } from './client';
import type { XamanPayloadResponse } from '../types/signing';

export type BundleExpirationOption = '1_day' | '3_days' | '7_days' | '30_days';

export type BundleStatus = 'pending' | 'live' | 'sold' | 'cancelled' | 'expired';

// websocketUrl stays required: bundle modals connect to it unconditionally.
export type XamanPayload = XamanPayloadResponse & { websocketUrl: string };

/** Phase 4 signing intent — carries client-signing txJson for joey-wc. */
export interface SigningIntentRef {
  intentId: string;
  provider: string;
  txJson?: Record<string, unknown>;
}

export interface BundleItem {
  id: string;
  bundleId: string;
  nftTokenId: string;
  sellOfferIndex?: string;
  status: 'pending' | 'confirmed' | 'transferred';
  offerTxHash?: string;
  createdAt: string;
  // NFT details (joined)
  nft_name?: string;
  nft_image_url?: string;
  nft_thumbnail_url?: string;
  rarity_rank?: number;
}

export interface NFTBundle {
  id: string;
  sellerAddress: string;
  collectionId: string;
  priceDrops: string;
  priceXrp: number;
  status: BundleStatus;
  buyerAddress?: string;
  paymentTxHash?: string;
  expiresAt?: string;
  soldAt?: string;
  cancelledAt?: string;
  createdAt: string;
  updatedAt: string;
  // Joined data
  collectionName?: string;
  collectionImageUrl?: string;
  items: BundleItem[];
  itemCount: number;
}

export interface BundleStats {
  liveCount: number;
  soldCount: number;
  totalVolumeXrp: number;
  lowestPriceXrp: number | null;
}

export interface CreateBundleParams {
  collectionId: string;
  nftTokenIds: string[];
  priceXrp: number;
  expiration?: BundleExpirationOption;
  userAddress: string;
}

export interface CreateBundleResponse {
  bundleId: string;
  bundle: NFTBundle;
  payloads: Array<{
    nftTokenId: string;
    payload?: XamanPayload;
    signingIntent?: SigningIntentRef;
  }>;
}

export interface ConfirmOfferParams {
  bundleId: string;
  nftTokenId: string;
  offerTxHash: string;
}

export interface ConfirmOfferResponse {
  bundleComplete: boolean;
  bundleStatus?: BundleStatus;
}

export interface PurchaseBundleResponse {
  payload?: XamanPayload;
  signingIntent?: SigningIntentRef;
  priceXrp: number;
}

export interface ExecutePurchaseParams {
  bundleId: string;
  buyerAddress: string;
  paymentTxHash: string;
}

export interface ExecutePurchaseResponse {
  bundleId: string;
  transferOfferIndices?: string[];
  message: string;
}

export interface CancelBundleResponse {
  bundleId: string;
  cancelPayloads: Array<{
    nftTokenId: string;
    payload?: XamanPayload;
    signingIntent?: SigningIntentRef;
  }>;
  message: string;
}

export interface BundleListResponse {
  bundles: NFTBundle[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Create a new bundle
 * Returns Xaman payloads for each NFT (seller signs these to list)
 */
export async function createBundle(params: CreateBundleParams): Promise<CreateBundleResponse> {
  const response = await apiClient.post<ApiResponse<CreateBundleResponse>>(
    '/bundles/create',
    params
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to create bundle');
  }

  return response.data;
}

/**
 * Confirm an individual NFT offer has been signed
 * Called after seller signs each NFTokenCreateOffer
 */
export async function confirmBundleOffer(params: ConfirmOfferParams): Promise<ConfirmOfferResponse> {
  const response = await apiClient.post<ApiResponse<ConfirmOfferResponse>>(
    '/bundles/confirm-offer',
    params
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to confirm bundle offer');
  }

  return response.data;
}

/**
 * Get live bundles for a collection
 */
export async function getCollectionBundles(
  collectionId: string,
  options: { limit?: number; offset?: number; orderBy?: 'price_asc' | 'price_desc' | 'created_desc' | 'price_per_nft_asc' | 'price_per_nft_desc' } = {}
): Promise<BundleListResponse> {
  const params = new URLSearchParams();
  if (options.limit) params.set('limit', options.limit.toString());
  if (options.offset) params.set('offset', options.offset.toString());
  if (options.orderBy) params.set('orderBy', options.orderBy);

  const response = await apiClient.get<ApiResponse<BundleListResponse>>(
    `/bundles/collection/${collectionId}?${params}`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to get collection bundles');
  }

  return response.data;
}

/**
 * Get bundle statistics for a collection
 */
export async function getCollectionBundleStats(collectionId: string): Promise<BundleStats> {
  const response = await apiClient.get<ApiResponse<BundleStats>>(
    `/bundles/collection/${collectionId}/stats`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to get bundle stats');
  }

  return response.data;
}

/**
 * Get bundle details by ID
 */
export async function getBundleDetails(bundleId: string): Promise<{ bundle: NFTBundle }> {
  const response = await apiClient.get<ApiResponse<{ bundle: NFTBundle }>>(
    `/bundles/${bundleId}`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to get bundle details');
  }

  return response.data;
}

/**
 * Create purchase payment payload for buyer
 */
export async function createBundlePurchasePayload(
  bundleId: string,
  userAddress: string
): Promise<PurchaseBundleResponse> {
  const response = await apiClient.post<ApiResponse<PurchaseBundleResponse>>(
    `/bundles/${bundleId}/buy`,
    { userAddress }
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to create purchase payload');
  }

  return response.data;
}

/**
 * Execute bundle purchase after payment is confirmed
 */
export async function executeBundlePurchase(params: ExecutePurchaseParams): Promise<ExecutePurchaseResponse> {
  const response = await apiClient.post<ApiResponse<ExecutePurchaseResponse>>(
    `/bundles/${params.bundleId}/execute`,
    {
      buyerAddress: params.buyerAddress,
      paymentTxHash: params.paymentTxHash,
    }
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to execute purchase');
  }

  return response.data;
}

/**
 * Cancel a bundle
 * Returns Xaman payloads for cancelling each NFT offer
 */
export async function cancelBundle(bundleId: string, userAddress: string): Promise<CancelBundleResponse> {
  const response = await apiClient.post<ApiResponse<CancelBundleResponse>>(
    `/bundles/${bundleId}/cancel`,
    { userAddress }
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to cancel bundle');
  }

  return response.data;
}

/**
 * Get bundles the user is selling
 */
export async function getUserSellingBundles(
  options: { limit?: number; offset?: number; status?: BundleStatus[] } = {}
): Promise<BundleListResponse> {
  const params = new URLSearchParams();
  if (options.limit) params.set('limit', options.limit.toString());
  if (options.offset) params.set('offset', options.offset.toString());
  if (options.status?.length) params.set('status', options.status.join(','));

  const response = await apiClient.get<ApiResponse<BundleListResponse>>(
    `/bundles/user/selling?${params}`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to get selling bundles');
  }

  return response.data;
}

/**
 * Get bundles the user has purchased
 */
export async function getUserPurchasedBundles(
  options: { limit?: number; offset?: number } = {}
): Promise<BundleListResponse> {
  const params = new URLSearchParams();
  if (options.limit) params.set('limit', options.limit.toString());
  if (options.offset) params.set('offset', options.offset.toString());

  const response = await apiClient.get<ApiResponse<BundleListResponse>>(
    `/bundles/user/purchased?${params}`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to get purchased bundles');
  }

  return response.data;
}

/**
 * Check if an NFT is in an active bundle
 */
export async function checkNFTBundleStatus(nftTokenId: string): Promise<{
  inBundle: boolean;
  bundleId?: string;
}> {
  const response = await apiClient.get<ApiResponse<{ inBundle: boolean; bundleId?: string }>>(
    `/bundles/nft/${nftTokenId}/status`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to check NFT bundle status');
  }

  return response.data;
}

/**
 * Get expiration options for bundles
 */
export function getBundleExpirationOptions(): Array<{
  value: BundleExpirationOption;
  label: string;
}> {
  return [
    { value: '1_day', label: '1 Day' },
    { value: '3_days', label: '3 Days' },
    { value: '7_days', label: '7 Days' },
    { value: '30_days', label: '30 Days' },
  ];
}
